import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Loader2, CheckCircle2, ShieldCheck, KeyRound, Eye, EyeOff, Lock, Mail, AlertCircle } from "lucide-react";
import { useAuth } from "./AuthContext";
import { formatAuthErrorMessage } from "./authErrorUtils";
import { API_BASE } from "../config/api";
import { OtpCodeInput } from "../components/ui/OtpCodeInput";

interface ForgotPasswordPageProps {
  onGoLogin: () => void;
  isModal?: boolean;
  onClose?: () => void;
}

function PasswordStrength({ password }: { password: string }) {
  const checks = [
    { label: "8 caractères minimum", ok: password.length >= 8 },
    { label: "Une majuscule", ok: /[A-Z]/.test(password) },
    { label: "Un chiffre ou symbole", ok: /[0-9!@#$%^&*]/.test(password) },
  ];
  const score = checks.filter((c) => c.ok).length;
  const barColors = ["bg-red-400", "bg-amber-400", "bg-emerald-500"];
  const textColors = ["text-red-500", "text-amber-500", "text-emerald-600"];
  const labels = ["Faible", "Moyen", "Fort"];
  if (!password) return null;
  return (
    <div className="space-y-2 mt-2">
      <div className="flex gap-1">
        {[0, 1, 2].map((i) => (
          <div key={i} className={`h-1 flex-1 rounded-full transition-all ${i < score ? barColors[score - 1] : "bg-slate-200"}`} />
        ))}
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        {checks.map((c) => (
          <span key={c.label} className={`text-[10px] flex items-center gap-1 font-medium ${c.ok ? "text-emerald-600" : "text-slate-400"}`}>
            {c.ok ? <CheckCircle2 className="w-3 h-3" /> : <span className="w-3 h-3 text-slate-300">✕</span>}
            {c.label}
          </span>
        ))}
        {score > 0 && (
          <span className={`ml-auto text-[10px] font-bold ${textColors[score - 1]}`}>{labels[score - 1]}</span>
        )}
      </div>
    </div>
  );
}

export function ForgotPasswordPage({ onGoLogin, isModal, onClose }: ForgotPasswordPageProps) {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [showNewPw, setShowNewPw] = useState(false);
  const [showConfirmPw, setShowConfirmPw] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Resend state for Step 2
  const [resendTimer, setResendTimer] = useState(60);
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  const { clearError } = useAuth();

  useEffect(() => {
    if (!isModal) {
      window.scrollTo({ left: 0, top: 0, behavior: 'instant' });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }
    clearError();
    setError(null);
    return () => {
      clearError();
      setError(null);
    };
  }, [clearError, isModal]);

  // Compte à rebours renvoi de code OTP
  useEffect(() => {
    let timer: any;
    if (step === 2 && resendTimer > 0) {
      timer = setInterval(() => {
        setResendTimer((prev) => (prev > 0 ? prev - 1 : 0));
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [step, resendTimer]);

  const handleGoLogin = () => {
    clearError();
    setError(null);
    onGoLogin();
  };

  // Formatage mm:ss pour le timer
  const formatTimer = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  // Étape 1 : Demande de code OTP par email
  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!email || !email.includes("@")) {
      setError("Veuillez saisir une adresse e-mail valide.");
      return;
    }
    setIsLoading(true);
    try {
      const res = await fetch(`${API_BASE}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() })
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Impossible d\'envoyer le code de sécurité');
      }
      setStep(2);
      setResendTimer(60);
      setOtp("");
    } catch (err: any) {
      setError(formatAuthErrorMessage(err.message, "Une erreur est survenue. Veuillez réessayer."));
    } finally {
      setIsLoading(false);
    }
  };

  // Étape 2 : Vérification du code OTP (avancer vers l'étape nouveau mot de passe)
  const handleVerifyOtp = async (codeToVerify?: string) => {
    const code = codeToVerify || otp;
    setError(null);
    if (!code || code.length !== 6) {
      setError("Le code de vérification doit comporter 6 chiffres.");
      return;
    }
    setIsLoading(true);
    try {
      const res = await fetch(`${API_BASE}/auth/forgot-password/verify-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          otp: code.trim()
        })
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Code de vérification incorrect ou expiré');
      }
      setStep(3);
    } catch (err: any) {
      setError(formatAuthErrorMessage(err.message, "Code invalide ou expiré."));
    } finally {
      setIsLoading(false);
    }
  };

  // Renvoyer le code OTP
  const handleResendCode = async () => {
    if (resendTimer > 0 || isResending) return;
    setIsResending(true);
    setResendSuccess(false);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() })
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || "Impossible de renvoyer le code.");
      }
      setResendTimer(60);
      setResendSuccess(true);
      setTimeout(() => setResendSuccess(false), 5000);
    } catch (err: any) {
      setError(formatAuthErrorMessage(err.message, "Erreur lors du renvoi du code."));
    } finally {
      setIsResending(false);
    }
  };

  // Étape 3 : Soumission du nouveau mot de passe
  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (newPassword.length < 8) {
      setError("Le mot de passe doit contenir au moins 8 caractères.");
      return;
    }
    if (newPassword !== confirmPw) {
      setError("Les mots de passe ne correspondent pas.");
      return;
    }
    setIsLoading(true);
    try {
      const res = await fetch(`${API_BASE}/auth/forgot-password/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          otp: otp.trim(),
          newPassword
        })
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Impossible de réinitialiser le mot de passe');
      }
      setIsSuccess(true);
    } catch (err: any) {
      setError(formatAuthErrorMessage(err.message, "Une erreur est survenue. Veuillez réessayer."));
    } finally {
      setIsLoading(false);
    }
  };

  if (isSuccess) {
    return (
      <div className={isModal ? "w-full" : "min-h-screen min-h-[100dvh] w-full flex flex-col justify-center items-center bg-white px-4 sm:px-10 py-6 sm:py-10"}>
        <div className={isModal ? "w-full space-y-4 py-2 text-center" : "w-full max-w-[400px] my-auto space-y-7 py-4 text-center"}>
          {/* Logo */}
          {!isModal && (
            <div className="text-center flex flex-col items-center">
              <Link to="/" className="inline-block cursor-pointer transition-transform hover:scale-[1.02]">
                <img src="/logo.webp" alt="Zeyilo" className="h-10 sm:h-11 w-auto object-contain mb-1.5" />
              </Link>
              <p className="text-slate-500 text-sm">Agent IA WhatsApp</p>
            </div>
          )}

          <div className="space-y-3">
            <div className="w-14 h-14 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center mx-auto">
              <CheckCircle2 className="w-7 h-7" />
            </div>
            <h1 className="text-2xl font-bold text-slate-950 tracking-tight">Mot de passe mis à jour !</h1>
            <p className="text-slate-500 text-sm leading-relaxed">
              Votre mot de passe a été réinitialisé avec succès. Vous pouvez maintenant vous connecter en toute sécurité.
            </p>
          </div>

          <button
            type="button"
            onClick={handleGoLogin}
            className="w-full bg-slate-900 hover:bg-slate-800 text-white font-bold text-sm py-3 px-4 rounded-xl flex items-center justify-center gap-2 transition-all cursor-pointer shadow-none"
          >
            <ArrowLeft className="w-4 h-4" />
            Se connecter
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={isModal ? "w-full" : "min-h-screen min-h-[100dvh] w-full flex flex-col justify-center items-center bg-white px-4 sm:px-10 py-6 sm:py-10"}>
      <div className={isModal ? "w-full space-y-4" : "w-full max-w-[400px] my-auto space-y-7 py-2"}>
        
        {/* Logo */}
        {!isModal && (
          <div className="text-center flex flex-col items-center">
            <Link to="/" className="inline-block cursor-pointer transition-transform hover:scale-[1.02]">
              <img src="/logo.webp" alt="Zeyilo" className="h-10 sm:h-11 w-auto object-contain mb-1.5" />
            </Link>
            <p className="text-slate-500 text-sm">Agent IA WhatsApp</p>
          </div>
        )}

        {/* ================= ÉTAPE 1 : Saisie de l'adresse email (Design Bento) ================= */}
        {step === 1 && (
          <div className="space-y-2.5 animate-in fade-in duration-200">
            {/* Header Bento : Uniquement en pleine page */}
            {!isModal && (
              <div className="bg-slate-100/80 rounded-2xl p-3.5 sm:p-4 border-0 shadow-none">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-white text-emerald-600 flex items-center justify-center shrink-0 border-0 shadow-none">
                    <KeyRound className="w-5 h-5 shrink-0" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h2 className="text-xs sm:text-sm font-bold text-slate-900 leading-tight">
                      Mot de passe oublié ?
                    </h2>
                    <p className="text-[11px] text-slate-500 mt-0.5 leading-snug">
                      Recevez un code de sécurité à 6 chiffres par e-mail
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Sous-Bento : Saisie de l'email sans border */}
            <div className={`p-3.5 sm:p-4 border-0 shadow-none space-y-2.5 ${isModal ? 'bg-slate-50/80 rounded-2xl' : 'bg-white rounded-xl sm:rounded-2xl'}`}>
              <form onSubmit={handleSendOtp} className="space-y-2.5">
                {error && (
                  <div className="p-2 bg-rose-50 text-rose-700 text-xs font-medium rounded-xl flex items-center gap-2 border-0 animate-in fade-in">
                    <AlertCircle className="w-3.5 h-3.5 text-rose-500 shrink-0" />
                    <span>{error}</span>
                  </div>
                )}

                <div className="space-y-1">
                  <label className="text-[11px] font-bold text-slate-700">Adresse e-mail</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="vous@votreboutique.com"
                    required
                    autoFocus
                    className="w-full px-3 py-2 bg-white rounded-xl text-xs sm:text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-900 border-0 shadow-2xs transition-all"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-slate-900 hover:bg-black active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold text-xs sm:text-sm py-2 sm:py-2.5 px-4 rounded-full flex items-center justify-center gap-1.5 transition-all cursor-pointer shadow-xs border-0 mt-1"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      <span>Envoi du code...</span>
                    </>
                  ) : (
                    <>
                      <ShieldCheck className="w-3.5 h-3.5" />
                      <span>Envoyer le code de vérification</span>
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Bento Card : Guide de récupération */}
            <div className="bg-slate-100/80 rounded-2xl p-3 sm:p-3.5 border-0 shadow-none space-y-2">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                <span className="text-[11px] font-bold text-slate-700">Procédure de récupération :</span>
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center gap-2.5 text-[11px] text-slate-600 bg-white/70 rounded-lg px-2.5 py-1.5">
                  <span className="w-4 h-4 rounded-md bg-white text-slate-700 text-[10px] font-bold flex items-center justify-center shrink-0 shadow-2xs">1</span>
                  <span>Saisissez votre adresse e-mail ci-dessus</span>
                </div>
                <div className="flex items-center gap-2.5 text-[11px] text-slate-600 bg-white/70 rounded-lg px-2.5 py-1.5">
                  <span className="w-4 h-4 rounded-md bg-white text-slate-700 text-[10px] font-bold flex items-center justify-center shrink-0 shadow-2xs">2</span>
                  <span>Recevez un code de validation à 6 chiffres par e-mail</span>
                </div>
                <div className="flex items-center gap-2.5 text-[11px] text-slate-600 bg-white/70 rounded-lg px-2.5 py-1.5">
                  <span className="w-4 h-4 rounded-md bg-white text-slate-700 text-[10px] font-bold flex items-center justify-center shrink-0 shadow-2xs">3</span>
                  <span>Définissez votre nouveau mot de passe sécurisé</span>
                </div>
              </div>
            </div>

            {/* Footer Bento : Retour connexion */}
            <div className="p-3 bg-slate-100/80 rounded-2xl text-center text-xs text-slate-600 border-0">
              <span>Vous vous souvenez de votre mot de passe ? </span>
              <button
                type="button"
                onClick={handleGoLogin}
                className="text-emerald-600 hover:text-emerald-700 font-bold transition-colors cursor-pointer"
              >
                Se connecter
              </button>
            </div>
          </div>
        )}

        {/* ================= ÉTAPE 2 : Saisie du code OTP (Design Bento) ================= */}
        {step === 2 && (
          <div className="space-y-3 sm:space-y-3.5 animate-in fade-in slide-in-from-right-3 duration-200">
            {/* Header Bento : Forgot Password Status */}
            <div className="bg-slate-100/80 rounded-2xl p-3.5 sm:p-4 border-0 shadow-none">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-white text-emerald-600 flex items-center justify-center shrink-0 border-0 shadow-none">
                  <KeyRound className="w-5 h-5 shrink-0" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <h2 className="text-xs sm:text-sm font-bold text-slate-900 leading-tight">
                      Réinitialisation de mot de passe
                    </h2>
                    <span className="px-1.5 py-0.2 rounded-full text-[9px] font-bold bg-amber-500 text-white">
                      Sécurité
                    </span>
                  </div>
                  <p className="text-[11px] text-slate-500 mt-0.5 leading-snug">
                    Code de sécurité envoyé par e-mail
                  </p>
                </div>
              </div>

              {email && (
                <div className="mt-2.5 pt-2.5 border-t border-slate-200/50 flex items-center justify-between text-[11px]">
                  <span className="text-slate-500">Compte concerné :</span>
                  <span className="font-semibold text-slate-800 truncate max-w-[210px]">{email}</span>
                </div>
              )}
            </div>

            {/* Sous-Bento blanc : Saisie du code à 6 chiffres */}
            <div className="bg-white rounded-xl p-3.5 sm:p-4 border-0 shadow-none space-y-3">
              <div className="flex items-center justify-between gap-2">
                <span className="text-[11px] font-bold text-slate-700">
                  Code de vérification
                </span>
                <span className="text-[10px] text-slate-400">
                  Valide 30 min
                </span>
              </div>

              {error && (
                <div className="p-2.5 bg-rose-50 text-rose-700 text-xs font-medium rounded-xl flex items-center gap-2 border-0 animate-in fade-in">
                  <AlertCircle className="w-4 h-4 text-rose-500 shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              {resendSuccess && (
                <div className="p-2.5 bg-emerald-50 text-emerald-700 text-xs font-medium rounded-xl flex items-center gap-2 border-0 animate-in fade-in">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                  <span>Un nouveau code de vérification vous a été envoyé !</span>
                </div>
              )}

              {/* OTP Code 6-box input (Sans bordure verte) */}
              <OtpCodeInput
                value={otp}
                onChange={setOtp}
                onComplete={(code) => handleVerifyOtp(code)}
                disabled={isLoading}
                hasError={!!error}
                autoFocus={true}
              />

              {/* Countdown / Resend text */}
              <div className="text-center text-xs pt-0.5">
                <span className="text-slate-500">Vous n'avez pas reçu le code ? </span>
                {resendTimer > 0 ? (
                  <span className="font-semibold text-slate-700">
                    Renvoyer le code (dans {formatTimer(resendTimer)})
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={handleResendCode}
                    disabled={isResending}
                    className="font-semibold text-emerald-600 hover:text-emerald-700 hover:underline transition-colors inline-flex items-center gap-1 cursor-pointer disabled:opacity-50"
                  >
                    {isResending ? (
                      <>
                        <Loader2 className="w-3 h-3 animate-spin" />
                        Envoi en cours...
                      </>
                    ) : (
                      "Renvoyer le code"
                    )}
                  </button>
                )}
              </div>

              {/* Submit Button */}
              <button
                type="button"
                onClick={() => handleVerifyOtp()}
                disabled={isLoading || otp.length !== 6}
                className="w-full bg-slate-900 hover:bg-slate-800 active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold text-xs sm:text-sm py-2.5 sm:py-3 px-4 rounded-xl flex items-center justify-center gap-2 transition-all cursor-pointer shadow-xs border-0"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Vérification du code...</span>
                  </>
                ) : (
                  <>
                    <span>Valider le code</span>
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </div>

            {/* Bento Card : Protocole de réinitialisation */}
            <div className="bg-slate-100/80 rounded-2xl p-3 sm:p-3.5 border-0 shadow-none space-y-2">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                <span className="text-[11px] font-bold text-slate-700">Protocole de réinitialisation :</span>
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center gap-2.5 text-[11px] text-slate-600 bg-white/70 rounded-lg px-2.5 py-1.5">
                  <span className="w-4 h-4 rounded-md bg-white text-slate-700 text-[10px] font-bold flex items-center justify-center shrink-0 shadow-2xs">1</span>
                  <span>Consultez vos e-mails reçus sur <strong>{email}</strong></span>
                </div>
                <div className="flex items-center gap-2.5 text-[11px] text-slate-600 bg-white/70 rounded-lg px-2.5 py-1.5">
                  <span className="w-4 h-4 rounded-md bg-white text-slate-700 text-[10px] font-bold flex items-center justify-center shrink-0 shadow-2xs">2</span>
                  <span>Saisissez le code à 6 chiffres pour déverrouiller l'étape</span>
                </div>
                <div className="flex items-center gap-2.5 text-[11px] text-slate-600 bg-white/70 rounded-lg px-2.5 py-1.5">
                  <span className="w-4 h-4 rounded-md bg-white text-slate-700 text-[10px] font-bold flex items-center justify-center shrink-0 shadow-2xs">3</span>
                  <span>Définissez votre nouveau mot de passe sécurisé</span>
                </div>
              </div>
            </div>

            {/* Actions secondaires */}
            <div className="space-y-2 pt-1">
              <button
                type="button"
                onClick={() => {
                  setStep(1);
                  setError(null);
                }}
                className="w-full py-2.5 px-3 rounded-xl bg-white hover:bg-slate-50 text-slate-600 hover:text-slate-900 text-xs font-semibold transition-all cursor-pointer flex items-center justify-center gap-1.5 border border-slate-200/70 shadow-xs active:scale-[0.99]"
              >
                <ArrowLeft className="w-3.5 h-3.5 text-slate-400" />
                <span>Changer d'adresse e-mail</span>
              </button>

              <button
                type="button"
                onClick={handleGoLogin}
                className="w-full text-center text-xs font-semibold text-emerald-600 hover:text-emerald-700 flex items-center justify-center gap-1.5 py-1.5 transition-colors cursor-pointer"
              >
                <span>Retour à la connexion</span>
              </button>
            </div>
          </div>
        )}

        {/* ================= ÉTAPE 3 : Saisie du nouveau mot de passe (Design Bento) ================= */}
        {step === 3 && (
          <div className="space-y-3 sm:space-y-3.5 animate-in fade-in slide-in-from-right-3 duration-200">
            {/* Header Bento */}
            <div className="bg-slate-100/80 rounded-2xl p-3.5 sm:p-4 border-0 shadow-none">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-white text-emerald-600 flex items-center justify-center shrink-0 border-0 shadow-none">
                  <Lock className="w-5 h-5 shrink-0" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <h2 className="text-xs sm:text-sm font-bold text-slate-900 leading-tight">
                      Nouveau mot de passe
                    </h2>
                    <span className="px-1.5 py-0.2 rounded-full text-[9px] font-bold bg-emerald-500 text-white">
                      Étape finale
                    </span>
                  </div>
                  <p className="text-[11px] text-slate-500 mt-0.5 leading-snug">
                    Choisissez un mot de passe sécurisé pour votre compte
                  </p>
                </div>
              </div>

              {email && (
                <div className="mt-2.5 pt-2.5 border-t border-slate-200/50 flex items-center justify-between text-[11px]">
                  <span className="text-slate-500">Compte :</span>
                  <span className="font-semibold text-slate-800 truncate max-w-[210px]">{email}</span>
                </div>
              )}
            </div>

            {/* Formulaire Bento */}
            <form onSubmit={handleResetPassword} className="bg-white rounded-xl p-3.5 sm:p-4 border-0 shadow-none space-y-3.5">
              {error && (
                <div className="p-2.5 bg-rose-50 text-rose-700 text-xs font-medium rounded-xl flex items-center gap-2 border-0 animate-in fade-in">
                  <AlertCircle className="w-4 h-4 text-rose-500 shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-slate-700">Nouveau mot de passe *</label>
                <div className="relative">
                  <input
                    type={showNewPw ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    autoFocus
                    className="w-full px-3.5 py-2.5 border border-slate-200 rounded-xl text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-slate-900 transition-all pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPw(!showNewPw)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    {showNewPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <PasswordStrength password={newPassword} />
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-slate-700">Confirmer le nouveau mot de passe *</label>
                <div className="relative">
                  <input
                    type={showConfirmPw ? "text" : "password"}
                    value={confirmPw}
                    onChange={(e) => setConfirmPw(e.target.value)}
                    placeholder="••••••••"
                    required
                    className={`w-full px-3.5 py-2.5 border rounded-xl text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none transition-all pr-10 ${
                      confirmPw && confirmPw !== newPassword
                        ? "border-rose-300 focus:border-rose-400 focus:ring-1 focus:ring-rose-300"
                        : "border-slate-200 focus:border-slate-900 focus:ring-1 focus:ring-slate-900"
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPw(!showConfirmPw)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    {showConfirmPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {confirmPw && confirmPw !== newPassword && (
                  <p className="text-xs text-rose-500 font-medium">Les mots de passe ne correspondent pas.</p>
                )}
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-slate-900 hover:bg-slate-800 active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold text-xs sm:text-sm py-2.5 sm:py-3 px-4 rounded-xl flex items-center justify-center gap-2 transition-all cursor-pointer shadow-xs border-0 mt-2"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Mise à jour en cours...</span>
                  </>
                ) : (
                  <>
                    <KeyRound className="w-4 h-4" />
                    <span>Mettre à jour le mot de passe</span>
                  </>
                )}
              </button>
            </form>

            <div className="pt-1">
              <button
                type="button"
                onClick={handleGoLogin}
                className="w-full text-center text-xs font-semibold text-emerald-600 hover:text-emerald-700 flex items-center justify-center gap-1.5 py-1.5 transition-colors cursor-pointer"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                <span>Retour à la connexion</span>
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
